import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useRequestDownloadUrl,
  useRequestPreviewUrl,
} from '../../queries/documents';
import { ApiError } from '../../lib/api-error';
import { humanSize } from './human-size';
import type { Document } from '../../types/api';

/**
 * Minimal shape we need to render the modal. Lets callers pass either a
 * full `Document` or a runsheet attachment row (which lacks size/sha/etc.)
 * without forcing them to refetch the document.
 */
export type PreviewableDoc = Pick<Document, 'id' | 'content_type' | 'original_filename'> &
  Partial<
    Pick<Document, 'title' | 'file_size_bytes' | 'sha256' | 'updated_at'>
  >;

interface Props {
  opened: boolean;
  onClose: () => void;
  doc: PreviewableDoc | null;
}

interface PreviewState {
  url: string;
  filename: string | null;
  contentType: string;
}

/** Pixel height of the in-modal viewport for PDFs / images. */
const VIEWER_HEIGHT = 'calc(85vh - 120px)';

const INLINE_TYPES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/webp',
]);

/**
 * In-app preview modal. Calls the preview-url endpoint to get a presigned
 * inline GET URL, then renders by content-type. The "Download original"
 * button hits the separate download-url endpoint so the audit log records
 * a distinct action.
 */
export function DocumentPreviewModal({ opened, onClose, doc }: Props) {
  const previewMut = useRequestPreviewUrl();
  const downloadMut = useRequestDownloadUrl();

  const [state, setState] = useState<PreviewState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastDocKey, setLastDocKey] = useState<string | null>(null);

  // Adjust state during render: when the modal opens for a new doc, kick off
  // a preview fetch and clear any previous URL/error.
  const docKey = opened && doc ? `${doc.id}:${doc.updated_at ?? ''}` : null;
  if (docKey !== lastDocKey) {
    setLastDocKey(docKey);
    setState(null);
    setError(null);
    if (opened && doc) {
      void loadPreview(doc.id);
    }
  }
  // Keep eslint happy without an effect.
  useEffect(() => undefined, []);

  async function loadPreview(id: string) {
    try {
      const res = await previewMut.mutateAsync(id);
      setState({
        url: res.url,
        filename: res.filename,
        contentType: res.content_type ?? '',
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load preview.');
    }
  }

  async function handleDownload() {
    if (!doc) return;
    try {
      const res = await downloadMut.mutateAsync(doc.id);
      // A short-lived hidden anchor click triggers the browser's Save dialog
      // with the original filename. We avoid window.location.assign so the
      // current tab stays put.
      const a = document.createElement('a');
      a.href = res.url;
      if (res.filename) a.download = res.filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      notifications.show({
        title: 'Download failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  const isInline = !!doc?.content_type && INLINE_TYPES.has(doc.content_type);
  const isPdf = doc?.content_type === 'application/pdf';
  const isImage = doc?.content_type?.startsWith('image/') ?? false;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={doc?.original_filename ?? doc?.title ?? 'Document'}
      size="90%"
      centered
      padding="md"
      overlayProps={{ backgroundOpacity: 0.45 }}
    >
      <Stack gap="md">
        {doc && (
          <Group gap="xs" wrap="wrap">
            <Badge variant="light" tt="lowercase">
              {doc.content_type ?? '?'}
            </Badge>
            {doc.file_size_bytes != null && (
              <Badge variant="light" color="gray">
                {humanSize(Number(doc.file_size_bytes))}
              </Badge>
            )}
            {doc.sha256 && (
              <Badge variant="light" color="pine">
                sha256
              </Badge>
            )}
          </Group>
        )}

        {!isInline && doc && (
          <Alert
            icon={<IconAlertTriangle size={14} />}
            color="yellow"
            variant="light"
            radius="md"
          >
            Inline preview is not available for this file type. Use Download instead.
          </Alert>
        )}

        {error && (
          <Alert color="red" radius="md">
            {error}
          </Alert>
        )}

        <div style={{ minHeight: VIEWER_HEIGHT }}>
          {!state && !error && isInline && (
            <Center mih={VIEWER_HEIGHT}>
              <Loader />
            </Center>
          )}

          {state && isPdf && (
            <iframe
              title={state.filename ?? 'PDF preview'}
              src={state.url}
              style={{ width: '100%', height: VIEWER_HEIGHT, border: 0, borderRadius: 6 }}
            />
          )}

          {state && isImage && (
            <Center>
              <img
                src={state.url}
                alt={state.filename ?? 'document'}
                style={{ maxWidth: '100%', maxHeight: VIEWER_HEIGHT, objectFit: 'contain' }}
              />
            </Center>
          )}
        </div>

        <Group justify="space-between" mt="sm">
          <Text size="xs" c="dimmed">
            {state?.filename ?? doc?.original_filename ?? ''}
          </Text>
          <Group>
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
            <Button
              leftSection={<IconDownload size={14} />}
              onClick={handleDownload}
              loading={downloadMut.isPending}
              disabled={!doc}
            >
              Download original
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

